/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { 
  useFetchDebtSecurities,
  useCreateDebtSecurity, 
  useUpdateDebtSecurity, 
  useDeleteDebtSecurity,
} from '@/hooks/securities/debt/useDebtSecurities.ts';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Plus, Edit, Trash2, Calendar, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { DebtSecurityForm } from '@/components/debt-security-form/DebtSecurityForm';
import { calculateDaysUntilMaturity } from '@securityUtils';
import { SecuritiesTransactionForm, type SecuritiesTransactionFormData } from '@/components/securities-transaction-form/SecuritiesTransactionForm';
import { useFetchAccounts } from '@accountHooks';
import { useCreateSecuritiesTransaction } from '@/hooks/securities/useSecuritiesTransactions.ts';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import { DebtSecuritiesOverview } from '@/components/debt-securities-overview/DebtSecuritiesOverview';
import {DebtSecurity} from "@/integrations/supabase/debt.models.ts";

/**
 * DebtSecuritiesPage component displays and manages debt securities (bonds).
 * This is a dedicated page for debt securities accessible at /investments/securities/debt-securities.
 */
export default function DebtSecuritiesPage() {
  const navigate = useNavigate();

  // Dialog state
  const [debtFormOpen, setDebtFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingDebt, setEditingDebt] = useState<DebtSecurity | null>(null);
  const [deletingDebtId, setDeletingDebtId] = useState<string | null>(null);
  const [duplicateISINDialog, setDuplicateISINDialog] = useState<{
    open: boolean;
    isin: string;
    existingId: string;
    newData: any;
  }>({ open: false, isin: '', existingId: '', newData: null });
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch debt securities
  const { 
    data: debtSecurities = [], 
    isLoading, 
    error,
    refetch,
  } = useFetchDebtSecurities();

  const filteredDebtSecurities = useMemo(() => {
    if (!searchQuery) return debtSecurities;
    const lowerQuery = searchQuery.toLowerCase();
    return debtSecurities.filter(d => 
      d.isin?.toLowerCase().includes(lowerQuery) ||
      d.issuer?.name?.toLowerCase().includes(lowerQuery)
    );
  }, [debtSecurities, searchQuery]);

  // Fetch accounts for transaction form
  const { data: accounts = [] } = useFetchAccounts();

  // Mutation hooks
  const createDebt = useCreateDebtSecurity();
  const updateDebt = useUpdateDebtSecurity();
  const deleteDebt = useDeleteDebtSecurity();
  const createTransaction = useCreateSecuritiesTransaction();

  // Handler functions for action buttons
  const handleAddDebt = () => {
    setEditingDebt(null);
    setDebtFormOpen(true);
  };

  const handleDeposit = () => {
    setTransactionType('deposit');
    setTransactionFormOpen(true);
  };

  const handleWithdraw = () => {
    setTransactionType('withdrawal');
    setTransactionFormOpen(true);
  };

  const handleEditDebt = (debt: DebtSecurity) => {
    setEditingDebt(debt);
    setDebtFormOpen(true);
  };

  const handleDeleteDebt = (debtId: string) => {
    setDeletingDebtId(debtId);
  };

  const handleCardClick = (debtId: string) => {
    navigate(`/investments/securities/debt-securities/${debtId}`);
  };

  // Handler for saving debt (create or update)
  const handleSaveDebt = async (data: any) => {
    try {
      if (editingDebt) {
        // Update existing debt
        await updateDebt.mutateAsync({
          id: editingDebt.id,
          ...data,
        });
        toast.success('Debt security updated successfully');
        setStatusMessage('Debt security updated successfully');
        setDebtFormOpen(false);
        setEditingDebt(null);
      } else {
        // Check for duplicate ISIN before creating
        const existingDebt = debtSecurities.find(d => d.isin === data.isin);
        
        if (existingDebt) {
          // Show duplicate ISIN dialog
          setDuplicateISINDialog({
            open: true,
            isin: data.isin,
            existingId: existingDebt.id,
            newData: data,
          });
        } else {
          // Create new debt
          await createDebt.mutateAsync(data);
          toast.success('Debt security created successfully');
          setStatusMessage('Debt security created successfully');
          setDebtFormOpen(false);
        }
      }
    } catch (error) {
      console.error('Error saving debt:', error);
      const msg = editingDebt ? 'Failed to update debt security' : 'Failed to create debt security';
      toast.error(msg);
      setStatusMessage(msg);
    }
  };

  // Handler for adding to existing holding
  const handleAddToExisting = async () => {
    if (!duplicateISINDialog.newData || !duplicateISINDialog.existingId) return;

    try {
      const existingDebt = debtSecurities.find(d => d.id === duplicateISINDialog.existingId);
      if (!existingDebt) {
        toast.error('Existing debt security not found');
        return;
      }

      // Update quantity by adding new quantity to existing
      const newQuantity = existingDebt.quantity + duplicateISINDialog.newData.quantity;
      
      await updateDebt.mutateAsync({
        id: existingDebt.id,
        quantity: newQuantity,
      });
      
      toast.success('Added to existing holding successfully');
      setStatusMessage('Added to existing holding successfully');
      setDuplicateISINDialog({ open: false, isin: '', existingId: '', newData: null });
      setDebtFormOpen(false);
    } catch (error) {
      console.error('Error adding to existing holding:', error);
      toast.error('Failed to add to existing holding');
      setStatusMessage('Failed to add to existing holding');
    }
  };

  // Handler for creating new despite duplicate
  const handleCreateNewAnyway = async () => {
    if (!duplicateISINDialog.newData) return;

    try {
      await createDebt.mutateAsync(duplicateISINDialog.newData);
      toast.success('Debt security created successfully');
      setStatusMessage('Debt security created successfully');
      setDuplicateISINDialog({ open: false, isin: '', existingId: '', newData: null });
      setDebtFormOpen(false);
    } catch (error) {
      console.error('Error creating debt:', error);
      toast.error('Failed to create debt security');
      setStatusMessage('Failed to create debt security');
    }
  };

  // Handler for canceling debt form
  const handleCancelDebtForm = () => {
    setDebtFormOpen(false);
    setEditingDebt(null);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!deletingDebtId) return;
    
    try {
      await deleteDebt.mutateAsync(deletingDebtId);
      toast.success('Debt security deleted successfully');
      setStatusMessage('Debt security deleted successfully');
      setDeletingDebtId(null);
    } catch (error) {
      console.error('Error deleting debt:', error);
      toast.error('Failed to delete debt security');
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeletingDebtId(null);
  };

  // Handler for saving transaction
  const handleSaveTransaction = async (data: SecuritiesTransactionFormData) => {
    try {
      await createTransaction.mutateAsync({
        security_type: data.security_type,
        security_id: data.security_id,
        account_id: data.account_id,
        transaction_type: data.transaction_type,
        amount: data.amount,
        currency: data.currency,
        transaction_date: data.transaction_date,
        notes: data.notes || null,
      });
      const msg = `${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`;
      toast.success(msg);
      setStatusMessage(msg);
      setTransactionFormOpen(false);
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error('Failed to record transaction');
      setStatusMessage('Failed to record transaction');
    }
  };

  // Handler for canceling transaction form
  const handleCancelTransactionForm = () => {
    setTransactionFormOpen(false);
  };

  // Disable deposit/withdraw buttons when no debts exist
  const hasDebts = debtSecurities.length > 0;

  // Prepare securities list for transaction form
  const securitiesForTransaction = debtSecurities.map(debt => ({
    id: debt.id,
    label: `${debt.isin} - ${debt.issuer?.name || 'Unknown Issuer'}`,
    currency: debt.currency,
  }));

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load debt securities. Please try again."
            onRetry={() => refetch()}
          />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Screen reader live region for mutation status announcements */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Debt Securities</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Manage your bond holdings
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search bonds..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto" role="group" aria-label="Debt securities actions">
              <Button
                onClick={handleDeposit}
                disabled={!hasDebts}
                variant="outline"
                aria-label="Deposit funds to debt securities account"
                aria-disabled={!hasDebts}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={!hasDebts}
                variant="outline"
                aria-label="Withdraw funds from debt securities account"
                aria-disabled={!hasDebts}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Withdraw
              </Button>
              <Button
                onClick={handleAddDebt}
                aria-label="Add new debt security"
                className="min-h-[44px] w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Overview Section — only when securities exist */}
        {hasDebts && (
          <DebtSecuritiesOverview
            debtSecurities={filteredDebtSecurities}
            isLoading={false}
          />
        )}

        {/* Debt Cards List or Empty State */}
        {hasDebts ? (
          <div 
            className="space-y-4" 
            role="list" 
            aria-label="Debt securities"
            aria-live="polite"
          >
            {filteredDebtSecurities.map((debt) => {
              const daysUntilMaturity = calculateDaysUntilMaturity(debt.maturity_date);
              const isNearMaturity = daysUntilMaturity <= 30;

              return (
                <Card key={debt.id} className="hover:bg-accent/50 transition-colors cursor-pointer">
                  <CardContent className="p-4 sm:p-6">
                    <div
                      className="flex flex-col gap-4"
                      role="button"
                      tabIndex={0}
                      aria-label={`View details for ${debt.isin}`}
                      onClick={() => handleCardClick(debt.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCardClick(debt.id);
                        }
                      }}
                    >
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold">{debt.isin}</h3>
                            {isNearMaturity && (
                              <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 rounded">
                                Maturing Soon
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {debt.issuer?.name || 'Unknown Issuer'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Issue #{debt.issue_number}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleEditDebt(debt); }}
                            aria-label={`Edit ${debt.isin}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Edit className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteDebt(debt.id); }}
                            aria-label={`Delete ${debt.isin}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>

                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Face Value</p>
                          <p className="text-sm font-medium">{debt.currency} {debt.face_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Coupon Rate</p>
                          <p className="text-sm font-medium">{debt.coupon_rate.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                          <p className="text-sm font-medium">{debt.quantity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Nominal Price</p>
                          <p className="text-sm font-medium">{debt.currency} {debt.nominal_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" aria-hidden="true" />
                          <span>Maturity: {new Date(debt.maturity_date).toLocaleDateString()}</span>
                        </div>
                        <span>• Tenor: {debt.tenor}</span>
                        <span>• {daysUntilMaturity} days until maturity</span>
                        {debt.notes && <span>• {debt.notes}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-4">
                No debt securities yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Get started by adding your first bond holding
              </p>
              <Button 
                onClick={handleAddDebt} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add your first debt security"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Debt Dialog */}
        <Dialog open={debtFormOpen} onOpenChange={setDebtFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="debt-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingDebt ? 'Edit Debt Security' : 'Add Debt Security'}
              </DialogTitle>
              <DialogDescription id="debt-dialog-description">
                {editingDebt 
                  ? 'Update the details of your bond holding.' 
                  : 'Add a new bond holding to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <DebtSecurityForm
              debtSecurity={editingDebt}
              onSave={handleSaveDebt}
              onCancel={handleCancelDebtForm}
            />
          </DialogContent>
        </Dialog>

        {/* Duplicate ISIN Dialog */}
        <AlertDialog open={duplicateISINDialog.open} onOpenChange={(open) => !open && setDuplicateISINDialog({ open: false, isin: '', existingId: '', newData: null })}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="duplicate-isin-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Duplicate ISIN Detected</AlertDialogTitle>
              <AlertDialogDescription id="duplicate-isin-dialog-description">
                A bond with ISIN {duplicateISINDialog.isin} already exists in your portfolio. 
                Would you like to add to the existing holding or create a new entry?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={() => setDuplicateISINDialog({ open: false, isin: '', existingId: '', newData: null })} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleCreateNewAnyway}
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Create new entry"
              >
                Create New
              </AlertDialogAction>
              <AlertDialogAction 
                onClick={handleAddToExisting}
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add to existing holding"
              >
                Add to Existing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingDebtId} onOpenChange={(open) => !open && handleCancelDelete()}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the debt security
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of debt security"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transaction Dialog */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit to Debt Securities Account' : 'Withdraw from Debt Securities Account'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit' 
                  ? 'Record a deposit to your debt securities account.' 
                  : 'Record a withdrawal from your debt securities account.'}
              </DialogDescription>
            </DialogHeader>
            <SecuritiesTransactionForm
              transactionType={transactionType}
              securityType="debt"
              securities={securitiesForTransaction}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={handleCancelTransactionForm}
            />
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
}
